import type { ButtonHTMLAttributes, PropsWithChildren } from "react";

export interface ButtonTemplateProps extends PropsWithChildren, ButtonHTMLAttributes<HTMLButtonElement> {
    as?: "button" | "span";
    defaultColors?: string;
    outlineColors?: string;
    isDisabled?: boolean;
    loading?: boolean;
    outlined?: boolean;
    size?: "lg" | "md" | "sm";
}

export default function ButtonTemplate({
    defaultColors = "bg-teal-800 border-transparent text-white hover:bg-teal-700 hover:border-teal-700 active:bg-teal-700 focus:border-teal-700 focus:bg-teal-700",
    outlineColors = "bg-transparent border-teal-600 text-teal-600 hover:text-white hover:bg-teal-600 hover:border-teal-700 active:bg-teal-600 active:text-white focus:border-teal-600 focus:bg-teal-600 focus:text-white",
    as = "button",
    type = "submit",
    isDisabled = false,
    loading = false,
    outlined = false,
    size = "md",
    className = "",
    children,
    disabled,
    ...rest
}: ButtonTemplateProps) {
    const classList = [
        "ease-in-out transition border focus:ring-offset-2 duration-150 focus:outline-none focus:ring-2 leading-tight font-semibold text-center inline-flex items-center justify-center",
    ];

    if (size === "lg") {
        classList.push("px-5 py-2 rounded-lg tracking-widest");
    } else if (size === "md") {
        classList.push("text-xs px-4 py-1.5 rounded-md uppercase tracking-widest");
    } else if (size === "sm") {
        classList.push("text-[0.7rem] px-3 py-1 uppercase rounded-md tracking-widest");
    }

    classList.push(outlined ? outlineColors : defaultColors);

    if (loading || isDisabled) {
        classList.push("opacity-70");
    }

    if (className) {
        classList.push(className);
    }

    const resolvedDisabled = Boolean(loading || isDisabled || disabled);
    const Component = as === "span" ? "span" : "button";

    return (
        <Component
            {...(Component === "button" ? { type, disabled: resolvedDisabled } : {})}
            className={classList.join(" ")}
            {...rest}
        >
            {children}
        </Component>
    );
}
